/* eslint-disable @typescript-eslint/no-explicit-any */
// FIXME: fix any
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '../calendar';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { format } from 'date-fns';
import {
  Token,
  TokenDefinition,
  OperationDefinition,
} from '@/components/ui/TokenizedInput/types';
import { getDefaultOperationsForType } from './operations';
import * as React from 'react';

// TODO: get rid of all any types
// TODO: render description of operation

interface TokenEditFormProps {
  token: Token;
  tokenDefinition: TokenDefinition;
  operationDefinitions: OperationDefinition[];
  onSave: (updatedToken: Token) => void;
  onCancel: () => void;
  onDelete: () => void;
}

export function TokenEditForm({
  token,
  tokenDefinition,
  operationDefinitions,
  onSave,
  onCancel,
  onDelete,
}: TokenEditFormProps) {
  const [operation, setOperation] = useState(token.operation);
  const [value, setValue] = useState<any>(token.value);
  const [asyncOptions, setAsyncOptions] = useState<any[] | null>(null);

  const availableOperations =
    tokenDefinition.operations ||
    getDefaultOperationsForType(tokenDefinition.type);

  useEffect(() => {
    if (tokenDefinition.type === 'select' && tokenDefinition.fetchOptions) {
      tokenDefinition
        .fetchOptions(value || '')
        .then(setAsyncOptions)
        .catch(() => {
          setAsyncOptions([]);
        });
    } else if (tokenDefinition.type === 'select' && tokenDefinition.options) {
      setAsyncOptions(tokenDefinition.options);
    }
  }, [tokenDefinition, value]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      field: token.field,
      operation: operation.trim(),
      value: value.toString().trim(),
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      const target = e.target as HTMLElement;
      if (
        target.tagName !== 'INPUT' ||
        target.getAttribute('type') !== 'text'
      ) {
        e.preventDefault();
        handleSubmit(e as any);
      }
    }
  };

  const handleDateSelect = (date: Date) => {
    const formattedDate = format(date, 'yyyy-MM-dd');
    setValue(formattedDate);
  };

  const handleOptionSelect = (option: any) => {
    const optionValue =
      tokenDefinition.getOptionValue?.(option) || option.value || option;
    setValue(optionValue);
  };

  const renderValueInput = () => {
    switch (tokenDefinition.type) {
      case 'date':
        return (
          <div>
            <label className="text-foreground mb-1 block text-sm font-medium">
              Date
            </label>
            <CalendarComponent
              mode="single"
              selected={value ? new Date(value) : undefined}
              onSelect={(date) => {
                if (date) handleDateSelect(date);
              }}
            />
          </div>
        );

      case 'select': {
        const options = asyncOptions || tokenDefinition.options || [];
        return (
          <div>
            <label className="text-foreground mb-1 block text-sm font-medium">
              Value
            </label>
            <div className="max-h-32 space-y-1 overflow-y-auto">
              {options.map((option, index) => {
                const optionValue =
                  tokenDefinition.getOptionValue?.(option) ||
                  option.value ||
                  option;
                const optionLabel =
                  tokenDefinition.getOptionLabel?.(option) ||
                  option.label ||
                  option;
                const isSelected = value === optionValue;

                return (
                  <button
                    key={index}
                    type="button"
                    className={`hover:bg-accent w-full rounded px-2 py-1 text-left text-sm ${
                    isSelected ? 'bg-accent text-accent-foreground' : '' }`}
                    onClick={() => handleOptionSelect(option)}
                  >
                    {tokenDefinition.renderItem
                      ? tokenDefinition.renderItem(
                          option,
                          tokenDefinition,
                          token,
                        )
                      : optionLabel}
                  </button>
                );
              })}
            </div>
          </div>
        );
      }

      case 'text':
      default:
        return (
          <div>
            <label className="text-foreground mb-1 block text-sm font-medium">
              Value
            </label>
            <input
              type="text"
              name="value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className={cn(
                `border-input flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm
                shadow-xs transition-[color,box-shadow] outline-none`,
                'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
                'placeholder:text-muted-foreground',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
              placeholder={tokenDefinition.placeholder || 'Enter value'}
              required
            />
          </div>
        );
    }
  };

  return (
    <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
      {/* Field display (read-only) */}
      <div className="mb-4 flex items-center gap-3">
        {React.createElement(tokenDefinition.icon, { className: 'h-5 w-5' })}
        <h3 className="text-foreground text-lg font-semibold">
          {tokenDefinition.label || tokenDefinition.key}
        </h3>
      </div>

      {/* Operation selection */}
      <div className="mt-3">
        <label className="text-foreground mb-1 block text-sm font-medium">
          Operation
        </label>
        <RadioGroup
          value={operation}
          onValueChange={setOperation}
          name="operation"
        >
          {availableOperations.map((opKey) => {
            const opDef = operationDefinitions.find((od) => od.key === opKey);

            return (
              <div key={opKey} className="flex items-center space-x-2">
                <RadioGroupItem value={opKey} id={opKey} className="peer" />
                <label
                  htmlFor={opKey}
                  className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed
                    peer-disabled:opacity-70"
                >
                  {opDef?.label || opKey}
                </label>
              </div>
            );
          })}
        </RadioGroup>
      </div>

      {/* Value input (type-specific) */}
      <div className="mt-3">{renderValueInput()}</div>

      <div className="mt-3 flex gap-2">
        <div className="flex gap-2">
          <Button type="submit" size="sm">
            Save
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        </div>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          onClick={onDelete}
          className="ml-auto"
        >
          Delete
        </Button>
      </div>
    </form>
  );
}
